<template>
  <div>
    <v-row class="px-6 mb-4">
      <v-col cols="2">
        <v-text-field
          v-model="searchInputValue"
          prepend-inner-icon="$magnify"
          outlined
          dense
          single-line
          hide-details="auto"
          :placeholder="$t('tslContractors.searchPlaceholder')"
        />
      </v-col>
      <v-col cols="2" offset="4">
        <v-btn class="mr-2" outlined @click="clearFilters()" block>
          {{ $t('tslContractors.clearFilters') }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn color="primary" @click="openFilterDialog()" block depressed>
          {{ $t('tslContractors.filter') }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn color="primary" @click="addClient()" block depressed>
          {{ $t('tslContractors.addClient') }}
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="showEmptyMessage" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-magnify</v-icon>
      <div class="text-h6 grey--text">{{ $t('tslContractors.searchPlaceholder') }}</div>
    </div>

    <v-data-table
      v-else
      :items="contractors"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      :page="page"
      :items-per-page="pageSize"
    >
      <template v-slot:[`item.cooperationStatus`]="{ item }">
        <v-select
          v-if="$store.getters.isOwner || $store.getters.isManager"
          v-model="item.cooperationStatus"
          :items="cooperationStatuses"
          item-text="text"
          item-value="value"
          dense
          @change="updateCooperationStatus(item.id, item.cooperationStatus)"
        ></v-select>
        <div v-else>
          {{ getCooperationStatusText(item.cooperationStatus) }}
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item link @click="viewDetails(item)">
              <v-list-item-title>{{ $t("common.showProfile") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="pageCount > 1">
        <v-pagination v-model="page" :length="pageCount"></v-pagination>
      </template>
    </v-data-table>

    <TSLFilterDialog
      v-model="filterDialog"
      :filters="filters"
      @close="closeFilterDialog"
      @filter="filter"
    />
  </div>
</template>

<script>
import Service from '@/services'
import TSLFilterDialog from '@/views/TSL/Contractors/dialogs/TSLFilterDialog'

export default {
  name: 'ClientsTable',
  components: {
    TSLFilterDialog
  },
  data () {
    return {
      loading: false,
      contractors: [],
      searchInputValue: '',
      page: 1,
      pageSize: 20,
      totalCount: 0,
      filterDialog: false,
      filters: {
        countries: [],
        city: '',
        cooperationStatus: null
      },
      searchTimeout: null,
      headers: [
        { text: this.$t('tslContractors.companyName'), value: 'companyName' },
        { text: this.$t('tslContractors.vatNumber'), value: 'vatNumber' },
        { text: this.$t('tslContractors.city'), value: 'city' },
        { text: this.$t('tslContractors.country'), value: 'country' },
        { text: this.$t('tslContractors.cooperationStatus'), value: 'cooperationStatus' },
        { text: '', value: 'actions', sortable: false }
      ],
      cooperationStatuses: [
        { text: this.$t('tslContractors.active'), value: 1 },
        { text: this.$t('tslContractors.suspended'), value: 2 }
      ]
    }
  },
  computed: {
    pageCount () {
      return Math.ceil(this.totalCount / this.pageSize)
    },
    hasActiveFilters () {
      return this.filters.countries.length > 0 ||
             this.filters.city ||
             this.filters.cooperationStatus !== null
    },
    canLoadData () {
      return this.searchInputValue.length >= 3 || this.hasActiveFilters
    },
    showEmptyMessage () {
      return !this.canLoadData
    }
  },
  watch: {
    page () {
      if (this.canLoadData) {
        this.loadContractors()
      }
    },
    searchInputValue () {
      this.page = 1
      clearTimeout(this.searchTimeout)

      if (this.searchInputValue.length >= 3 || this.searchInputValue.length === 0) {
        this.loading = true
        this.searchTimeout = setTimeout(() => {
          this.loadContractors()
        }, 1000)
      } else {
        this.contractors = []
        this.totalCount = 0
      }
    }
  },
  created () {
    // Nie ładuj danych automatycznie przy inicjalizacji
  },
  methods: {
    async loadContractors () {
      if (!this.canLoadData) {
        this.contractors = []
        this.totalCount = 0
        this.loading = false
        return
      }

      this.loading = true
      try {
        const params = {
          type: 1, // Client
          page: this.page,
          pageSize: this.pageSize,
          search: this.searchInputValue || null,
          countries: JSON.stringify(this.filters.countries),
          city: this.filters.city || null,
          cooperationStatus: this.filters.cooperationStatus
        }
        const { data } = await Service.get('/TSLContractor/GetContractors', params)
        this.contractors = data.results
        this.totalCount = data.count
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error loading clients')
      } finally {
        this.loading = false
      }
    },
    async updateCooperationStatus (contractorId, status) {
      try {
        await Service.put('/TSLContractor/UpdateCooperationStatus', {
          contractorId,
          cooperationStatus: status
        })
        this.$store.dispatch('setSuccessNotification', this.$t('tslContractors.cooperationStatusUpdated'))
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error updating status')
        this.loadContractors()
      }
    },
    getCooperationStatusText (status) {
      const item = this.cooperationStatuses.find(s => s.value === status)
      return item ? item.text : ''
    },
    clearFilters () {
      this.searchInputValue = ''
      this.filters = {
        countries: [],
        city: '',
        cooperationStatus: null
      }
      this.page = 1
      this.loadContractors()
    },
    openFilterDialog () {
      this.filterDialog = true
    },
    closeFilterDialog () {
      this.filterDialog = false
    },
    filter (filters) {
      this.filters = filters
      this.page = 1
      this.loadContractors()
      this.filterDialog = false
    },
    addClient () {
      this.$router.push('/tsl/contractors/add/client')
    },
    viewDetails (item) {
      this.$router.push(`/tsl/contractors/${item.id}`)
    }
  }
}
</script>
